import { Filter } from '@fabric-ui/components';
import { FileFormat } from '@fabric-ui/components/Table';
import { DeliveryConfig } from '../../Reports/types';
import {
  Bar100PercentHorizontalTypeWidget,
  Bar100PercentVerticalTypeWidget,
  BarHorizontalTypeWidget,
  BarStackedHorizontalTypeWidget,
  BarStackedVerticalTypeWidget,
  BarVerticalTypeWidget,
} from './BarWidget.types';
import { WidgetCategory, WidgetDefinition } from './base.types';
import { LineTypeWidget, SplineTypeWidget } from './LineWidget.types';
import { DonutTypeWidget, PieTypeWidget, PieWithNeedleTypeWidget } from './PieWidget.types';
import { DwQueryRequest, ExpressionTypes, Field, SortBy } from './QueryObject.types';
import { PivotTableTypeWidget, TableCustomDefinition, TableSubType, TableTypeWidget, TableWidget } from './TableWidget.types';
import { TextTypeWidget } from './TextWidget.types';
import { ScoreTypeWidget, TileTypeWidget, TrendTypeWidget } from './TileWidget.types';
import { VennTypeWidget } from './VennWidget.types';

export type BarTypeWidget =
  | BarStackedHorizontalTypeWidget
  | BarStackedVerticalTypeWidget
  | BarHorizontalTypeWidget
  | BarVerticalTypeWidget
  | Bar100PercentHorizontalTypeWidget
  | Bar100PercentVerticalTypeWidget;

export type AllTypeWidgets =
  // PIE
  | DonutTypeWidget
  | PieTypeWidget
  | PieWithNeedleTypeWidget
  // TABLE
  | TableTypeWidget
  | PivotTableTypeWidget
  // BAR
  | BarTypeWidget
  // LINE
  | LineTypeWidget
  | SplineTypeWidget
  // TILE
  | TileTypeWidget
  | ScoreTypeWidget
  | TrendTypeWidget
  // TEXT
  | TextTypeWidget
  /** Venn - not supported for Custom Dashboard */
  | VennTypeWidget;

export type WidgetByCategory<T extends WidgetCategory> = Extract<AllTypeWidgets, { category: T }>;
export type WidgetCategoryType<T extends WidgetCategory> = WidgetByCategory<T>['type'];
export type WidgetByType<T extends WidgetType> = Extract<AllTypeWidgets, { type: T }>;
export type WidgetCustomByType<T extends WidgetType> = WidgetByType<T>['definition']['custom'];
export type WidgetCustomByCategory<T extends WidgetCategory> = WidgetByCategory<T>['definition']['custom'];
export type WidgetType = AllTypeWidgets['type'];

export type QueryObject = Omit<DwQueryRequest, 'select'> & {
  select: {
    dims: (Omit<Field, 'alias' | 'expression'> | { expression: ExpressionTypes; alias: string })[];
    metrics: Field[];
  };
};

export type SavedPivotTable = Omit<TableWidget, 'requests'> & {
  type: TableSubType.PivotTable;
  requests: QueryObject[];
  definition: WidgetDefinition & { custom: TableCustomDefinition & { showColTotals: boolean; sortingCol?: SortBy[] } };
};

export type SavedAllTypeWidgets = Omit<AllTypeWidgets, 'requests'> & {
  requests: QueryObject[];
};

export const publicId = 'PUBLIC';
export enum PermissionType {
  private = 'PRIVATE',
  publicEdit = 'PUBLIC_EDIT',
  publicView = 'PUBLIC_VIEW',
  shared = 'SHARED',
}
export enum UserPermissionType {
  editor = 'EDITOR',
  viewer = 'VIEWER',
}
export type DashboardReportPermissionSetLink = {
  permissionSetId: string;
  sharedType: UserPermissionType;
};
export type DashboardReportPermission = {
  viewers: string[];
  editors: string[];
  type: PermissionType;
  permissionSets?: DashboardReportPermissionSetLink[];
};

export type DashboardSharingType = 'PUBLIC' | 'PRIVATE' | 'SHARED';
export type DashboardSharingLevel = 'VIEWER' | 'EDITOR';
export type DashboardSharingSource = 'DIRECT' | 'TAG';

export type DashboardSharing = {
  type: DashboardSharingType;
  users: Array<{ id: string; level: DashboardSharingLevel }>;
  permissionSets: Array<{
    id: string;
    label: string;
    level: DashboardSharingLevel;
    source: DashboardSharingSource;
  }>;
};

export type SharingFieldValue = {
  kind: 'SHARING_TYPE' | 'USER' | 'PERMISSION_SET';
  value: string;
  label: string;
};

export type CustomDashboardDto = {
  id?: string;
  name: string;
  description?: string;
  tags: string[];
  apps?: string[];
  dashboardReportPermission?: DashboardReportPermission;
  sharing?: DashboardSharing | null;
  type: ReportsTypes;
  intervalExpression: string;
  createdByUserId?: string;
  updatedByUserId?: string;
  sharedUsers?: string[];
  updatedAt?: string;
  widgets: AllTypeWidgets[];
  active: boolean;
  deliveryConfig?: DeliveryConfig;
  userPermissionType?: UserPermissionType;
  customQueries?: null;
  predefined?: boolean;
  globalFilters?: Filter;
  lastRunAt?: string;
  isIntegrationActive?: any;
  omitAliasPrefix?: boolean;
  allowViewersToChangeFilters?: boolean;
};

export type DashboardReportsPage = {
  content: CustomDashboardDto[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
  empty: boolean;
};

export enum ReportsTypes {
  TABULAR = 'TABULAR',
  Visual = 'VISUAL',
}

export enum DashboardType {
  CUSTOM_DASHBOARD = 'CUSTOM_DASHBOARD',
  REMEDIATION_DASHBOARD = 'REMEDIATION_DASHBOARD',
  RISK_DASHBOARD = 'RISK_DASHBOARD',
  THREAT_DASHBOARD = 'THREAT_DASHBOARD',
  ACCOUNT_USAGE = 'ACCOUNT_USAGE',
  IDNP_REMEDIATION_DASHBOARD = 'IDNP_REMEDIATION_DASHBOARD',
  IDNP_OVERVIEW_DASHBOARD = 'IDNP_OVERVIEW_DASHBOARD',
}

export type DashboardExportDto = {
  reportId?: string;
  dashboardType: DashboardType;
  fileFormat: FileFormat;
  searchParams?: string;
  themeMode?: string;
};
